import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveGradient } from './flipcard-gradient';
import { VondiLogo } from './VondiLogo';

/**
 * FlipCard — la tarjeta líquida de VONDI (FLIPCARD_SPEC_v3, manual §12).
 *
 * "El formato es sagrado. La jerarquía (oferta → marca) y el acabado líquido
 * nunca cambian. Lo único que cambia es el color, según la marca aliada, y el
 * texto."
 *
 * Este componente es la única definición de la tarjeta en todo el proyecto.
 * Antes el mismo visual estaba copiado en cinco archivos, cada uno con su
 * propia deriva; si vuelve a duplicarse, la regla de oro deja de sostenerse.
 *
 * Lo que NO es configurable, porque son constantes del manual: la proporción,
 * el radio, los márgenes, la posición de las zonas, los tamaños de fuente, la
 * capa de gel y el logo vondi.
 */

/* ── Geometría de la tarjeta ──────────────────────────────────────────────
   Todo el contenido se dibuja dentro de un SVG con viewBox 700×1000 (la
   proporción 7:10 del manual). Esa es la razón de fondo por la que la tarjeta
   se ve idéntica a cualquier tamaño sin medir nada en JavaScript: el navegador
   escala el viewBox, y las zonas son sub-SVG que ajustan su contenido solos. */
const VB_W = 700;
const VB_H = 1000;

/* Margen seguro: 8% del ancho por lado (spec §1). */
const MARGEN = VB_W * 0.08;
const ANCHO_UTIL = VB_W - MARGEN * 2;

/* Las tres zonas fijas, en porcentaje del alto (spec §2). */
const ZONA_A = { y: VB_H * 0.06, h: VB_H * 0.03 }; // logo vondi, arriba a la derecha
const ZONA_B = { y: VB_H * 0.24, h: VB_H * 0.24 }; // la oferta
/*
  Zona C — logo del aliado.

  El spec la fija en 0.16 del alto y aquí se sube a 0.22 (12-ago-2026, pedido de
  Ricardo: los logos se veían diminutos). Es una desviación consciente del spec
  y conviene saber por qué no bastaba con el spec:

    · La causa principal era **el dato, no la caja**. Los logos venían en
      lienzos de 1536×1024 donde el logo visible ocupaba entre el 2% y el 14%;
      como la zona escala la imagen entera, el logo aterrizaba minúsculo. Eso se
      arregló recortando los archivos (`php artisan tiendas:recortar-logos`), y
      solo eso ya los agrandó entre 1.2× y 5.3×.
    · Lo que suma esta caja es para los logos **verticales** —Psycho Bunny,
      Tous—, que se limitan por alto y no por ancho: con 0.16 desperdiciaban la
      franja vacía que la tarjeta tiene debajo.

  Sigue habiendo margen: la zona termina en 0.84 y la tarjeta en 1.0.
*/
const ZONA_C = { y: VB_H * 0.62, h: VB_H * 0.22 }; // logo del aliado

/* Tamaños de la jerarquía del titular, en unidades de diseño del bloque.
   Solo importan sus proporciones entre sí: el bloque completo se re-escala
   después para llenar la zona B. */
const T_NUMERO = 100;
/*
  El fino estaba en 45 y la muestra de Iván (`flipcards_SAMPLE.png`) pide 55.

  Se midió sobre la tarjeta «la cueva» del propio archivo: el número mide 24px
  de altura de mayúscula y «de descuento» 14px de ascendente. Convertido a
  tamaño de fuente eso da una proporción de ~0.55, no 0.45 — por eso él lo veía
  «muy chico y diferente». No es una preferencia: estaba mal medido.
*/
const T_FINO = 55;
const T_FUERTE = 100;
const T_ALCANCE = 30;
const INTERLINEA = 100; // ≈ 1.0 respecto del número: "muy pegados", rasgo de marca
const SEPARACION_ALCANCE = 52;

const POPPINS = "'Poppins', 'SF Pro Display', 'Segoe UI', Roboto, system-ui, sans-serif";

/* Capa de gel. Vive en public/ y se referencia con PUBLIC_URL para que la app
   siga funcionando si se sirve bajo un subdirectorio. */
const GLOSS_URL = `url(${process.env.PUBLIC_URL || ''}/assets/vondi-gloss.png)`;

/**
 * Ajusta un grupo SVG a su caja midiendo su bounding box real.
 *
 * Es la implementación de la regla crítica del spec §4: el titular escala hasta
 * llenar la caja sin desbordar, texto corto = letra más grande, y la caja nunca
 * crece. Al devolver el viewBox del propio contenido, el `preserveAspectRatio`
 * del sub-SVG hace el resto.
 *
 * Se vuelve a medir cuando cargan las fuentes: medir con la tipografía de
 * respaldo daría una caja equivocada y las tarjetas quedarían de distinto
 * tamaño entre sí.
 */
function useAjusteACaja(deps) {
  const ref = useRef(null);
  const [viewBox, setViewBox] = useState(null);

  const medir = useCallback(() => {
    const nodo = ref.current;
    if (!nodo) return;
    try {
      const b = nodo.getBBox();
      if (b.width > 0 && b.height > 0) {
        setViewBox(`${b.x} ${b.y} ${b.width} ${b.height}`);
      }
    } catch {
      /* getBBox lanza si el nodo no está renderizado; se reintenta al montar. */
    }
  }, []);

  useLayoutEffect(medir, [medir, ...deps]);

  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return undefined;
    let vigente = true;
    document.fonts.ready.then(() => {
      if (vigente) medir();
    });
    return () => {
      vigente = false;
    };
  }, [medir]);

  return { ref, viewBox };
}

/**
 * El wordmark de vondi dibujado en SVG para la zona A.
 *
 * La "o" es un anillo hueco, no la letra. Se dibuja midiendo dónde cae ese
 * carácter dentro del texto ya compuesto, en vez de con posiciones fijas: así
 * el anillo cae en su sitio aunque cambie la fuente disponible.
 */
function WordmarkSvg({ alto }) {
  const textoRef = useRef(null);
  const [anillo, setAnillo] = useState(null);
  const tamano = 100; // unidades internas; el sub-SVG lo escala a la zona

  const medir = useCallback(() => {
    const nodo = textoRef.current;
    if (!nodo || typeof nodo.getStartPositionOfChar !== 'function') return;
    try {
      const ini = nodo.getStartPositionOfChar(1); // la "o" de "vondi"
      const fin = nodo.getEndPositionOfChar(1);
      const ancho = fin.x - ini.x;
      if (ancho > 0) {
        // El anillo se centra en el hueco de la "o", a la altura de la x.
        const diametro = ancho * 0.86;
        setAnillo({
          cx: ini.x + ancho / 2,
          cy: ini.y - tamano * 0.26,
          r: diametro / 2,
          grosor: tamano * 0.085,
        });
      }
    } catch {
      /* Si el navegador no puede medir todavía, se reintenta tras las fuentes. */
    }
  }, []);

  useLayoutEffect(medir, [medir]);
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts) return undefined;
    let vigente = true;
    document.fonts.ready.then(() => {
      if (vigente) medir();
    });
    return () => {
      vigente = false;
    };
  }, [medir]);

  return (
    <svg
      x={MARGEN}
      y={ZONA_A.y}
      width={ANCHO_UTIL}
      height={alto}
      viewBox="0 0 300 120"
      /* Arriba a la derecha (spec §2). */
      preserveAspectRatio="xMaxYMid meet"
      overflow="visible"
    >
      <text
        ref={textoRef}
        x="0"
        y="90"
        fontFamily={POPPINS}
        fontSize={tamano}
        fontWeight="700"
        letterSpacing="-1"
        fill="#FFFFFF"
      >
        {/* La "o" se dibuja transparente: su lugar lo ocupa el anillo. */}
        v<tspan fillOpacity="0">o</tspan>ndi
      </text>
      {anillo && (
        <circle
          cx={anillo.cx}
          cy={anillo.cy}
          r={anillo.r}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={anillo.grosor}
        />
      )}
    </svg>
  );
}

/**
 * La cara frontal: el gel con la oferta.
 */
function CaraFrontal({ benefit, estado, className, style }) {
  const {
    headlineNumber,
    headlineThin,
    headlineStrong,
    scope,
    allyLogo,
    storeName,
  } = benefit;

  const gradiente = resolveGradient(benefit);

  /* El bloque del titular y el alcance se ajustan juntos como una sola unidad.
     Por eso todas las tarjetas conservan el mismo ritmo aunque digan cosas de
     largo muy distinto: lo que se re-escala es la composición entera, no cada
     línea por su cuenta. */
  const tieneLinea1 = Boolean(headlineNumber || headlineThin);
  const tieneFuerte = Boolean(headlineStrong);
  const tieneAlcance = Boolean(scope);

  /* Las líneas ausentes no dejan hueco: cada una solo ocupa sitio si existe
     (spec §3, "el bloque se recentra sin dejar hueco"). Se calculan las líneas
     base una tras otra; el `getBBox` posterior recorta lo que sobre. */
  const hayTitular = tieneLinea1 || tieneFuerte;
  let cursor = 0;
  const yLinea1 = tieneLinea1 ? cursor : null;
  if (tieneLinea1) cursor += INTERLINEA;
  const yFuerte = tieneFuerte ? cursor : null;
  if (tieneFuerte) cursor += INTERLINEA;
  const yAlcance = tieneAlcance
    ? hayTitular
      ? cursor - INTERLINEA + SEPARACION_ALCANCE
      : 0
    : null;

  const { ref: grupoRef, viewBox: viewBoxTitular } = useAjusteACaja([
    headlineNumber,
    headlineThin,
    headlineStrong,
    scope,
  ]);

  return (
    <div
      className={cn(
        'vd-flipcard',
        estado === 'bloqueada' && 'vd-flipcard--bloqueada',
        className
      )}
      style={{
        '--vd-card-start': gradiente.start,
        '--vd-card-end': gradiente.end,
        '--vd-card-angle': `${gradiente.angle}deg`,
        '--vd-gloss': GLOSS_URL,
        ...style,
      }}
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        width="100%"
        height="100%"
        style={{ display: 'block' }}
        aria-hidden="true"
      >
        {/* Zona A — logo vondi, siempre en blanco */}
        <WordmarkSvg alto={ZONA_A.h} />

        {/* Zona B — la oferta. Se ajusta a la caja; la caja nunca crece. */}
        <svg
          x={MARGEN}
          y={ZONA_B.y}
          width={ANCHO_UTIL}
          height={ZONA_B.h}
          viewBox={viewBoxTitular || `0 0 ${ANCHO_UTIL} ${ZONA_B.h}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <g ref={grupoRef} fill="#FFFFFF" fontFamily={POPPINS}>
            {tieneLinea1 && (
              <text x="0" y={yLinea1} textAnchor="middle">
                {headlineNumber && (
                  <tspan fontSize={T_NUMERO} fontWeight="700">
                    {headlineNumber}
                  </tspan>
                )}
                {headlineThin && (
                  <tspan
                    fontSize={T_FINO}
                    fontWeight="400"
                    dx={headlineNumber ? 8 : 0}
                  >
                    {headlineThin}
                  </tspan>
                )}
              </text>
            )}

            {tieneFuerte && (
              <text
                x="0"
                y={yFuerte}
                textAnchor="middle"
                fontSize={T_FUERTE}
                fontWeight="700"
              >
                {headlineStrong.toUpperCase()}
              </text>
            )}

            {tieneAlcance && (
              <text
                x="0"
                y={yAlcance}
                textAnchor="middle"
                fontSize={T_ALCANCE}
                fontWeight="400"
                fillOpacity="0.92"
              >
                {scope}
              </text>
            )}
          </g>
        </svg>

        {/* Zona C — logo del aliado, centrado y en blanco.
            `meet` hace el trabajo de contain: escala para caber, nunca deforma
            y nunca rebasa la caja (spec §4). */}
        {allyLogo && (
          <image
            href={allyLogo}
            x={MARGEN}
            y={ZONA_C.y}
            width={ANCHO_UTIL}
            height={ZONA_C.h}
            preserveAspectRatio="xMidYMid meet"
          />
        )}
      </svg>

      {/* Distintivos de estado. Van fuera del SVG para que hereden la
          tipografía y los tokens de la app. */}
      {/*
        El candado va en la franja libre entre el titular y el logo del aliado,
        no centrado en la tarjeta.

        Centrado caía justo encima del alcance —«en producto seleccionado»— y lo
        tapaba. La tarjeta tiene sus zonas repartidas por el manual: el titular
        acaba en el 48% del alto y el logo empieza en el 62%. Ese hueco es de
        nadie, y es donde el candado no estorba a nada.
      */}
      {estado === 'bloqueada' && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '48%',
            height: '14%',
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <Lock
            aria-hidden="true"
            style={{ width: '13%', height: 'auto', color: '#FFFFFF', opacity: 0.92 }}
          />
        </div>
      )}

      {estado === 'gratis' && (
        <span
          style={{
            position: 'absolute',
            top: '6%',
            left: '8%',
            padding: '0.35em 0.9em',
            borderRadius: 9999,
            background: 'rgba(255,255,255,0.92)',
            color: '#0E0E10',
            fontSize: 'clamp(9px, 4.2%, 13px)',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Gratis
        </span>
      )}

      {estado === 'canjeada' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(14,14,16,0.42)',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              border: '2px solid rgba(255,255,255,0.9)',
              color: '#FFFFFF',
              padding: '0.4em 1em',
              borderRadius: 9999,
              fontSize: 'clamp(10px, 5%, 16px)',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              transform: 'rotate(-8deg)',
            }}
          >
            Canjeado
          </span>
        </div>
      )}

      {/* El SVG es decorativo (aria-hidden); el texto accesible vive aquí. */}
      <span className="sr-only">
        {[storeName, headlineNumber, headlineThin, headlineStrong, scope]
          .filter(Boolean)
          .join(' ')}
      </span>
    </div>
  );
}

/**
 * La cara trasera: el detalle de la promoción.
 *
 * El manual no la especifica — solo describe el frente — así que se resuelve
 * con el sistema de BRAND.md: vidrio sobre el color de la tarjeta. Sin gel: el
 * acabado líquido es la firma de la cara frontal.
 */
function CaraTrasera({ benefit, onClose, className, style }) {
  const { storeName, description, terms, termsPdf, headlineStrong, headlineNumber } = benefit;
  const gradiente = resolveGradient(benefit);

  return (
    <div
      className={cn('vd-flipcard-face vd-flipcard-face--back', className)}
      style={{
        borderRadius: '14% / 9.8%',
        overflow: 'hidden',
        background: gradiente.css,
        boxShadow: '0 18px 40px -12px rgba(250, 77, 10, 0.35)',
        ...style,
      }}
    >
      {/*
        El panel de los términos: papel opaco, no vidrio.

        Era blanco al 90% con `blur(14px)`. Sobre el degradado de la marca, ese
        10% de transparencia teñía el texto y bajaba el contraste justo donde
        peor conviene — son las condiciones legales del beneficio, lo que alguien
        lee cuando va a discutir algo en el mostrador. Iván lo pidió sin
        transparencia (13-ago-2026) y tiene razón por legibilidad, no solo por
        gusto.
      */}
      <div
        style={{
          position: 'absolute',
          inset: '8%',
          borderRadius: 20,
          background: '#fff',
          border: '1.5px solid rgba(255,255,255,0.9)',
          padding: '7%',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5em',
          overflowY: 'auto',
          fontSize: 'clamp(9px, 4.1%, 13px)',
          color: '#33333A',
          lineHeight: 1.45,
          textAlign: 'left',
        }}
      >
        {/*
          El wordmark, que en el reverso no estaba.

          Iván pidió «logo de vondi más grande» en la cara volteada; al mirarlo,
          ahí no había ninguno — solo lo llevaba el frente. Así que se añade, y
          se pone donde no compite: arriba, junto al nombre de la tienda, para
          que la tarjeta siga siendo reconocible como de vondi también por atrás.

          Se usa `VondiLogo` y no el `WordmarkSvg` de la cara frontal: aquel es
          un `<svg>` anidado, con posición y color fijos para el lienzo de la
          tarjeta, y fuera de ese lienzo no se coloca. `VondiLogo` es el
          componente de marca pensado para HTML corriente.
        */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.6em',
          }}
        >
          {storeName && (
            <div
              className="font-display"
              style={{ fontSize: '1.35em', fontWeight: 700, color: '#0E0E10', lineHeight: 1.15 }}
            >
              {storeName}
            </div>
          )}
          <div style={{ flex: 'none' }}>
            <VondiLogo variant="gradiente" size={17} />
          </div>
        </div>

        {(headlineNumber || headlineStrong) && (
          <div style={{ fontWeight: 600, color: '#FA4D0A' }}>
            {[headlineNumber, headlineStrong].filter(Boolean).join(' ')}
          </div>
        )}

        {/*
          El reverso es para los términos, no un resumen de la oferta.

          Iván: «si le doy clic al descuento, ahí solo quiero que aparezcan
          todos los términos y condiciones». Antes la descripción comercial
          ocupaba el cuerpo y los términos quedaban al pie, en letra más chica
          que el resto — o sea, lo contrario de lo que la persona vino a leer.

          Ahora los términos son el contenido y ocupan el alto disponible con su
          propio desplazamiento, para que quepan completos por largos que sean.
        */}
        <div
          style={{
            fontSize: '0.85em',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#6E6E77',
            marginTop: '0.2em',
          }}
        >
          Términos y condiciones
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <p style={{ margin: 0, color: '#33333A', whiteSpace: 'pre-line' }}>
            {terms
              || description
              || 'Aplica sobre precio de outlet. Presenta tu QR de miembro en caja.'}
          </p>
        </div>

        {/*
          El documento completo, cuando la promoción lo tiene.

          Se abre aparte y no se intenta incrustar: unos términos de verdad
          traen vigencias, exclusiones y topes, y meter eso en el reverso de una
          tarjeta de teléfono sería ilegible. El visor del navegador permite
          acercar y guardar, que es lo que alguien necesita si va a discutir una
          condición en el mostrador.
        */}
        {termsPdf && (
          <a
            href={termsPdf}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4em',
              color: '#FA4D0A',
              fontWeight: 700,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              marginTop: '0.4em',
            }}
          >
            Ver documento completo
          </a>
        )}

        {onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="bg-grad-vivo text-white rounded-full font-semibold"
            style={{ padding: '0.6em 1em', border: 'none', cursor: 'pointer', marginTop: '0.6em' }}
          >
            Volver
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * La tarjeta completa, con su volteo.
 *
 * @param {object}  benefit    Datos del beneficio tal como los devuelve la API.
 * @param {string}  estado     'normal' | 'bloqueada' | 'gratis' | 'canjeada'
 * @param {boolean} flippable  Si false, la tarjeta no voltea (previews del admin).
 */
export function FlipCard({
  benefit,
  estado = 'normal',
  flippable = true,
  /** Cara con la que arranca. La vista previa del admin la usa para revisar el reverso. */
  caraInicial = 'frente',
  onClick,
  className = '',
  style = {},
  ...rest
}) {
  const [volteada, setVolteada] = useState(caraInicial === 'reverso');
  const prefiereMenosMovimiento = useReducedMotion();

  /* Una tarjeta ya canjeada no tiene acción (spec §6). */
  const interactiva = flippable && estado !== 'canjeada';

  const alternar = () => {
    if (onClick) return onClick(benefit);
    if (interactiva) setVolteada((v) => !v);
    return undefined;
  };

  return (
    <div
      className={cn('vd-flipcard-scene', className)}
      style={{ width: '100%', ...style }}
      {...rest}
    >
      <motion.div
        className="vd-flipcard-faces"
        animate={{ rotateY: volteada ? 180 : 0 }}
        /* Con movimiento reducido se cambia de cara sin animar el giro. */
        transition={
          prefiereMenosMovimiento
            ? { duration: 0 }
            : { type: 'spring', stiffness: 260, damping: 30 }
        }
        onClick={alternar}
        onKeyDown={(e) => {
          if (interactiva && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            alternar();
          }
        }}
        role={interactiva ? 'button' : undefined}
        tabIndex={interactiva ? 0 : undefined}
        aria-pressed={interactiva ? volteada : undefined}
        aria-label={interactiva ? `Ver detalle de ${benefit.storeName || 'la promoción'}` : undefined}
      >
        <CaraFrontal
          benefit={benefit}
          estado={estado}
          className="vd-flipcard-face"
          style={{ cursor: interactiva ? 'pointer' : 'default' }}
        />
        <CaraTrasera benefit={benefit} onClose={() => setVolteada(false)} />
      </motion.div>
    </div>
  );
}

export default FlipCard;
